export interface PubsubEvent<DataType> {
  name: string
  data: DataType
}

export type PubsubCallback<DataType> = (event: PubsubEvent<DataType>) => void

/**
 * Utility class for with pubsub functionality.
 * Mostly used by model classes so other modules can track state changes.
 */
export default class Pubsub<DataType> {
  private currentKey: number

  private subscriptions: {
    [eventName: string]: {
      [subscriptionKey: string]: PubsubCallback<DataType>
    }
  }

  constructor() {
    this.currentKey = 0
    this.subscriptions = {}
  }

  public publish = (event: PubsubEvent<DataType>): void => {
    const eventSubscription = this.subscriptions[event.name]

    if (eventSubscription) {
      Object.keys(eventSubscription).forEach((key: string) => {
        eventSubscription[key](event)
      })
    }
  }

  public subscribe = (eventName: string, subscription: PubsubCallback<DataType>): string => {
    if (!this.subscriptions[eventName]) {
      this.subscriptions[eventName] = {}
    }

    const nextKey = this.generateNextKey()
    this.subscriptions[eventName][nextKey] = subscription

    return `${eventName}.${nextKey}`
  }

  public unsubscribe(subscriptionKey: string): boolean {
    const [eventName, key] = subscriptionKey.split('.')

    if (!this.subscriptions[eventName] || !this.subscriptions[eventName][key]) {
      return false
    }

    delete this.subscriptions[eventName][key]

    return true
  }

  public unsubscribeAll(): void {
    this.subscriptions = {}
    this.currentKey = 0
  }

  private generateNextKey = () => {
    const nextKey = this.currentKey + 1
    this.currentKey = nextKey

    return nextKey
  }
}
